import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CollectionList } from 'types/interfaces/collectionList';
import { formatLanguage } from 'constants/languages';
import './WantListCardTooltip.css';
import { wantAcceptedFinishes } from 'utils/finishes';
import { FinishTag } from 'components/FinishTag';

// URL construction helpers
const getSetIconUrl = (code: string): string =>
  `https://trademagic-assets.s3.us-west-1.amazonaws.com/sets/icons/${code.toLowerCase()}.svg`;

const CONDITION_DISPLAY: Record<string, string> = {
  'NEAR_MINT': 'NM',
  'SLIGHTLY_PLAYED': 'SP',
  'MODERATELY_PLAYED': 'MP',
  'HEAVILY_PLAYED': 'HP',
  'DAMAGED': 'DMG',
};

interface WantListCardTooltipProps {
  cardData: CollectionList;
  position: { x: number; y: number };
  username: string;
  onClose: () => void;
}

export const WantListCardTooltip: React.FC<WantListCardTooltipProps> = ({
  cardData,
  position,
  username,
  onClose,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const [isVisible, setIsVisible] = useState(false);
  const [failedSetIcons, setFailedSetIcons] = useState<Set<string>>(new Set());

  // Debounce tooltip appearance (200ms delay)
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Edge detection for positioning
  useEffect(() => {
    if (!tooltipRef.current || !isVisible) return;

    const tooltip = tooltipRef.current;
    const rect = tooltip.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    let { x, y } = position;

    // Right edge detection
    if (x + rect.width > viewport.width - 10) {
      x = viewport.width - rect.width - 10;
    }

    // Bottom edge detection
    if (y + rect.height > viewport.height - 20) {
      y = viewport.height - rect.height - 20;
    }

    // Top edge detection
    if (y < 20) {
      y = 20;
    }

    setAdjustedPosition({ x, y });
  }, [position, isVisible]);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => onClose();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [onClose]);

  // Build set details from available data
  const setDetails = useMemo(() => {
    if (!cardData.acceptableSets || cardData.acceptableSets.length === 0) {
      return [];
    }
    return cardData.acceptableSets.map((setName, index) => ({
      name: setName,
      code: cardData.acceptableSetCodes?.[index] || setName.toLowerCase().replace(/\s+/g, ''),
    }));
  }, [cardData.acceptableSets, cardData.acceptableSetCodes]);

  // Determine finish display
  const finishBadges = useMemo(() => {
    // Show what the receiver WANTS (not intersected with physical availability)
    if (cardData.acceptFoil == null && cardData.acceptNonFoil == null && cardData.acceptEtched == null) return [];
    return wantAcceptedFinishes(cardData);
  }, [cardData]);

  const handleSetIconError = (code: string) => {
    setFailedSetIcons((prev) => new Set(prev).add(code));
  };

  if (!isVisible) {
    return null;
  }

  const tooltipContent = (
    <div
      className="want-list-tooltip-portal"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
    >
      <div className="want-list-tooltip" ref={tooltipRef}>
        {/* Username Header */}
        <div className="want-tooltip-header">
          <span className="want-tooltip-username">{username}</span>
          <span className="want-tooltip-wants"> wants:</span>
        </div>

        {/* Sets Section */}
        <div className="want-tooltip-section">
          <div className="want-tooltip-section-label">Sets</div>
          <div className="want-tooltip-set-icons">
            {setDetails.length > 0 ? (
              <>
                {setDetails.slice(0, 25).map((set) => (
                  !failedSetIcons.has(set.code) && (
                    <img
                      key={set.code}
                      src={getSetIconUrl(set.code)}
                      alt={set.name}
                      title={set.name}
                      className="want-tooltip-set-icon"
                      onError={() => handleSetIconError(set.code)}
                    />
                  )
                ))}
                {setDetails.length > 25 && (
                  <span className="want-tooltip-more-sets">
                    +{setDetails.length - 25} more
                  </span>
                )}
              </>
            ) : (
              <span className="want-tooltip-any-badge">Any set</span>
            )}
          </div>
        </div>

        {/* Finishes Section */}
        <div className="want-tooltip-section">
          <div className="want-tooltip-section-label">Finishes</div>
          <div className="want-tooltip-badges">
            {finishBadges.length > 0 ? (
              finishBadges.map((finish) => (
                <FinishTag key={finish} finish={finish} size="cell" />
              ))
            ) : (
              <span className="want-tooltip-any-badge">Any finish</span>
            )}
          </div>
        </div>

        {/* Languages Section */}
        <div className="want-tooltip-section">
          <div className="want-tooltip-section-label">Accepted Languages</div>
          <div className="want-tooltip-badges">
            {cardData.acceptableLanguages?.length ? (
              cardData.acceptableLanguages.map((lang) => (
                <span key={lang} className="want-tooltip-badge language">
                  {formatLanguage(lang)}
                </span>
              ))
            ) : (
              <span className="want-tooltip-any-badge">Any language</span>
            )}
          </div>
        </div>

        {/* Conditions Section */}
        {cardData.acceptableConditions?.length && (
          <div className="want-tooltip-section">
            <div className="want-tooltip-section-label">Conditions</div>
            <div className="want-tooltip-badges">
              {cardData.acceptableConditions.map((cond) => (
                <span key={cond} className="want-tooltip-badge condition">
                  {CONDITION_DISPLAY[cond] || cond}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(tooltipContent, document.body);
};

export default WantListCardTooltip;
